import type { Locale } from "./site";

// Human-readable republishing policy, localized per locale like privacy-policy.ts.
// Localized title/eyebrow/heading come from the message catalog; the body lives here.

const englishRepublishingPolicy = `
**Last updated:** June 2026

We want our work to travel. Most articles on the inext blog are published under a **Creative Commons Attribution 4.0 International (CC BY 4.0)** license — you are free to **republish, translate, excerpt, and adapt** them, including commercially, as long as you give credit.

## How to attribute

When you republish an inext article, please:

- Credit **inext** as the original author.
- Link back to the original article URL (the canonical link shown on the page and in our feeds).
- Indicate if you made changes — for example, that you shortened or translated it.

A simple credit line works well:

> "Originally published by inext at https://inext.ltd — republished under CC BY 4.0."

## The easy way: our feeds

Every post is available as a full-content feed, so you can syndicate automatically instead of copying by hand:

- **RSS:** https://inext.ltd/blog/feed.xml
- **Atom:** https://inext.ltd/blog/atom.xml
- **JSON Feed:** https://inext.ltd/blog/feed.json

Each entry carries the complete article HTML with absolute image URLs, the canonical link back to the original, and the license. Localized feeds live under each language prefix — for example, https://inext.ltd/ua/blog/feed.xml.

## Images

Hero and in-article images are covered by the same CC BY 4.0 license **unless a different credit is shown on the image itself**. Third-party logos and trademarks remain the property of their respective owners.

## Exceptions

A small number of posts are **republished from third parties with permission** — for example, an interview first published by another outlet. These are marked **"All rights reserved"** in the page footer and in the feed metadata, and are **not** available for republishing under CC BY 4.0. Please contact the original publisher for those.

## Questions

Not sure whether a specific piece is reusable, or need a higher-resolution asset? Email us at **info@inext.ltd** and we'll help.
`;

const ukrainianRepublishingPolicy = `
**Останнє оновлення:** червень 2026

Ми хочемо, щоб наші матеріали поширювалися. Більшість статей у блозі inext опубліковано за ліцензією **Creative Commons Attribution 4.0 International (CC BY 4.0)** — ви можете вільно **повторно публікувати, перекладати, цитувати та адаптувати** їх, зокрема в комерційних цілях, за умови зазначення джерела.

## Як зазначати авторство

Публікуючи статтю inext повторно, будь ласка:

- Зазначайте **inext** як першоджерело.
- Додавайте посилання на оригінальну URL-адресу статті (канонічне посилання, показане на сторінці та в наших стрічках).
- Зазначайте, якщо ви внесли зміни — наприклад, що скоротили чи переклали матеріал.

Підійде проста згадка:

> «Першоджерело — inext, https://inext.ltd. Матеріал опубліковано повторно за ліцензією CC BY 4.0.»

## Найпростіший спосіб: наші стрічки

Кожен допис доступний як стрічка з повним вмістом, тож ви можете поширювати матеріали автоматично, а не копіювати вручну:

- **RSS:** https://inext.ltd/blog/feed.xml
- **Atom:** https://inext.ltd/blog/atom.xml
- **JSON Feed:** https://inext.ltd/blog/feed.json

Кожен запис містить повний HTML статті з абсолютними URL-адресами зображень, канонічне посилання на оригінал і ліцензію. Локалізовані стрічки доступні за префіксом кожної мови — наприклад, https://inext.ltd/ua/blog/feed.xml.

## Зображення

Головні та внутрішні зображення статей поширюються за тією самою ліцензією CC BY 4.0, **якщо на самому зображенні не зазначено іншого авторства**. Логотипи й торгові марки третіх сторін залишаються власністю їхніх власників.

## Винятки

Невелику частину дописів **повторно опубліковано з дозволу третіх сторін** — наприклад, інтерв'ю, що спершу вийшло в іншому виданні. Такі матеріали позначено **«Усі права захищено»** в нижньому колонтитулі сторінки та в метаданих стрічки й **не** можна публікувати повторно за ліцензією CC BY 4.0. Щодо них звертайтеся до першоджерела.

## Запитання

Не впевнені, чи можна повторно використати конкретний матеріал, або потрібне зображення вищої роздільної здатності? Напишіть нам на **info@inext.ltd** — і ми допоможемо.
`;

const polishRepublishingPolicy = `
**Ostatnia aktualizacja:** czerwiec 2026

Chcemy, aby nasze materiały docierały dalej. Większość artykułów na blogu inext jest publikowana na licencji **Creative Commons Attribution 4.0 International (CC BY 4.0)** — możesz je swobodnie **republikować, tłumaczyć, cytować i adaptować**, również komercyjnie, pod warunkiem podania źródła.

## Jak podawać autora

Republikując artykuł inext, prosimy:

- Wskaż **inext** jako pierwotnego autora.
- Dodaj link do oryginalnego adresu URL artykułu (link kanoniczny widoczny na stronie i w naszych kanałach).
- Zaznacz, jeśli wprowadziłeś zmiany — na przykład, że skróciłeś lub przetłumaczyłeś tekst.

Wystarczy prosta informacja:

> „Pierwotnie opublikowano przez inext na https://inext.ltd — republikacja na licencji CC BY 4.0".

## Najprostszy sposób: nasze kanały

Każdy wpis jest dostępny jako kanał z pełną treścią, więc możesz syndykować materiały automatycznie zamiast kopiować je ręcznie:

- **RSS:** https://inext.ltd/blog/feed.xml
- **Atom:** https://inext.ltd/blog/atom.xml
- **JSON Feed:** https://inext.ltd/blog/feed.json

Każdy wpis zawiera pełny kod HTML artykułu z bezwzględnymi adresami URL obrazów, link kanoniczny do oryginału oraz licencję. Zlokalizowane kanały znajdują się pod prefiksem każdego języka — na przykład https://inext.ltd/ua/blog/feed.xml.

## Obrazy

Obrazy główne oraz te w treści artykułów są objęte tą samą licencją CC BY 4.0, **chyba że na samym obrazie wskazano inne źródło**. Loga i znaki towarowe podmiotów trzecich pozostają własnością ich właścicieli.

## Wyjątki

Niewielka liczba wpisów jest **republikowana od podmiotów trzecich za zgodą** — na przykład wywiad opublikowany pierwotnie w innym medium. Takie wpisy są oznaczone jako **„Wszelkie prawa zastrzeżone"** w stopce strony oraz w metadanych kanału i **nie** są dostępne do republikacji na licencji CC BY 4.0. W ich sprawie skontaktuj się z pierwotnym wydawcą.

## Pytania

Nie masz pewności, czy dany materiał można wykorzystać ponownie, albo potrzebujesz pliku w wyższej rozdzielczości? Napisz do nas na **info@inext.ltd** — pomożemy.
`;

const brazilianRepublishingPolicy = `
**Última atualização:** junho de 2026

Queremos que nosso conteúdo circule. A maioria dos artigos do blog da inext é publicada sob a licença **Creative Commons Attribution 4.0 International (CC BY 4.0)** — você pode **republicar, traduzir, citar e adaptar** os textos livremente, inclusive para fins comerciais, desde que dê os créditos.

## Como dar os créditos

Ao republicar um artigo da inext, por favor:

- Credite a **inext** como autora original.
- Inclua um link para a URL original do artigo (o link canônico exibido na página e em nossos feeds).
- Indique se você fez alterações — por exemplo, que resumiu ou traduziu o texto.

Uma linha de crédito simples já resolve:

> "Publicado originalmente pela inext em https://inext.ltd — republicado sob CC BY 4.0."

## O jeito fácil: nossos feeds

Cada post está disponível como um feed de conteúdo completo, então você pode sindicar automaticamente em vez de copiar manualmente:

- **RSS:** https://inext.ltd/blog/feed.xml
- **Atom:** https://inext.ltd/blog/atom.xml
- **JSON Feed:** https://inext.ltd/blog/feed.json

Cada entrada traz o HTML completo do artigo com URLs de imagem absolutas, o link canônico para o original e a licença. Feeds localizados ficam sob o prefixo de cada idioma — por exemplo, https://inext.ltd/br/blog/feed.json.

## Imagens

As imagens de destaque e as do corpo dos artigos estão cobertas pela mesma licença CC BY 4.0, **a não ser que um crédito diferente apareça na própria imagem**. Logotipos e marcas de terceiros continuam sendo propriedade de seus respectivos donos.

## Exceções

Um pequeno número de posts é **republicado de terceiros com permissão** — por exemplo, uma entrevista publicada originalmente em outro veículo. Esses posts são marcados como **"Todos os direitos reservados"** no rodapé da página e nos metadados do feed, e **não** estão disponíveis para republicação sob CC BY 4.0. Para esses casos, entre em contato com o veículo original.

## Dúvidas

Não tem certeza se um material específico pode ser reutilizado, ou precisa de um arquivo em resolução maior? Escreva para **info@inext.ltd** e nós ajudamos.
`;

const spanishRepublishingPolicy = `
**Última actualización:** junio de 2026

Queremos que nuestro contenido llegue lejos. La mayoría de los artículos del blog de inext se publican bajo la licencia **Creative Commons Attribution 4.0 International (CC BY 4.0)** — puede **republicar, traducir, citar y adaptar** los textos libremente, incluso con fines comerciales, siempre que dé el crédito correspondiente.

## Cómo dar el crédito

Al republicar un artículo de inext, por favor:

- Acredite a **inext** como autor original.
- Incluya un enlace a la URL original del artículo (el enlace canónico que aparece en la página y en nuestros feeds).
- Indique si hizo cambios — por ejemplo, que lo acortó o lo tradujo.

Una línea de crédito simple funciona bien:

> "Publicado originalmente por inext en https://inext.ltd — republicado bajo CC BY 4.0."

## La forma fácil: nuestros feeds

Cada publicación está disponible como un feed de contenido completo, así que puede sindicar automáticamente en lugar de copiar a mano:

- **RSS:** https://inext.ltd/blog/feed.xml
- **Atom:** https://inext.ltd/blog/atom.xml
- **JSON Feed:** https://inext.ltd/blog/feed.json

Cada entrada incluye el HTML completo del artículo con URLs de imagen absolutas, el enlace canónico al original y la licencia. Los feeds localizados están bajo el prefijo de cada idioma — por ejemplo, https://inext.ltd/ua/blog/feed.xml.

## Imágenes

Las imágenes destacadas y las del cuerpo de los artículos están cubiertas por la misma licencia CC BY 4.0, **a menos que se muestre un crédito diferente en la propia imagen**. Los logotipos y las marcas de terceros siguen siendo propiedad de sus respectivos dueños.

## Excepciones

Un pequeño número de publicaciones se **republica de terceros con permiso** — por ejemplo, una entrevista publicada originalmente en otro medio. Estas publicaciones están marcadas como **"Todos los derechos reservados"** en el pie de página y en los metadatos del feed, y **no** están disponibles para republicación bajo CC BY 4.0. Para esos casos, contacte al medio original.

## Preguntas

¿No está seguro de si un material específico se puede reutilizar, o necesita un archivo en mayor resolución? Escríbanos a **info@inext.ltd** y le ayudamos.
`;

export const republishingPolicies: Partial<Record<Locale, string>> = {
  en: englishRepublishingPolicy,
  uk: ukrainianRepublishingPolicy,
  pl: polishRepublishingPolicy,
  pt: brazilianRepublishingPolicy,
  es: spanishRepublishingPolicy,
};

export function getRepublishingPolicy(locale: Locale): string {
  return (republishingPolicies[locale] ?? englishRepublishingPolicy).trim();
}
